/**
 * "Talk to a human" card.
 *
 * Shown where a donor is most likely to want one: straight after paying, and
 * on Help. The channels are the same server-configured ones the Contact
 * screen and the Home band use, so there is one place to change who answers.
 *
 * Renders nothing when no channel is configured, rather than an empty card
 * promising support that does not exist.
 */

import { memo, useCallback } from 'react';
import { Pressable, StyleSheet, View } from 'react-native';

import type { SupportContact } from '@mfa/domain';
import { colors, radius, sizing, space } from '../../theme';
import { openDialler, openMail, openWebsite, openWhatsApp } from '../../core/linking/contact';
import { Glyph } from '../common/Icon';
import { Text } from '../common/Text';
import { useT } from '../../core/i18n/LanguageProvider';

export interface SupportCardProps {
  support?: SupportContact;
  title?: string;
  /** Quoted into the opening message so support knows what it is about. */
  reference?: string;
  testID?: string;
}

export const SupportCard = memo(function SupportCard({
  support,
  title = 'Need help?',
  reference,
  testID = 'support-card',
}: SupportCardProps) {
  const t = useT();
  const whatsapp = support?.whatsapp?.trim();
  const phone = support?.phone?.trim();
  const email = support?.email?.trim();
  const website = support?.website?.trim();

  const about = reference ? ` about donation ${reference}` : '';

  const onWhatsApp = useCallback(() => {
    if (whatsapp) void openWhatsApp(whatsapp, `Hello, I have a question${about}.`);
  }, [whatsapp, about]);
  const onEmail = useCallback(() => {
    if (email) void openMail(email, `Meal4ALL enquiry${about}`);
  }, [email, about]);
  const onCall = useCallback(() => {
    if (phone) void openDialler(phone);
  }, [phone]);
  const onWebsite = useCallback(() => {
    if (website) void openWebsite(website);
  }, [website]);

  // The value is shown, not just the channel name. Someone who has just paid
  // and wants to ask about it should be able to read the number off the
  // screen — or write it down — without tapping through to find out what it
  // is, and on a desktop browser a tel: link may open nothing at all.
  const actions = [
    whatsapp && {
      key: 'whatsapp', icon: 'chat', label: t('WhatsApp'), value: whatsapp, onPress: onWhatsApp,
    },
    phone && { key: 'call', icon: 'phone', label: t('Call'), value: phone, onPress: onCall },
    email && { key: 'email', icon: 'email', label: t('Email'), value: email, onPress: onEmail },
    website && {
      key: 'website', icon: 'globe', label: t('Website'), value: website, onPress: onWebsite,
    },
  ].filter(Boolean) as {
    key: string; icon: string; label: string; value: string; onPress: () => void;
  }[];

  if (actions.length === 0) return null;

  return (
    <View style={styles.card} testID={testID}>
      <Text variant="cardTitle">{title}</Text>
      {support?.hours ? (
        <Text variant="caption" tone="secondary">
          {support.hours}
        </Text>
      ) : null}
      <View style={styles.row}>
        {actions.map((action) => (
          <Pressable
            key={action.key}
            onPress={action.onPress}
            accessibilityRole="button"
            accessibilityLabel={`${action.label}: ${action.value}`}
            testID={`${testID}-${action.key}`}
            style={({ pressed }) => [styles.action, pressed && styles.pressed]}
          >
            {/* The icon names the channel; the label above the value said the
                same thing twice and cost a line per row. A screen reader still
                hears it — it is in accessibilityLabel above. */}
            <Glyph name={action.icon} size={sizing.icon.sm} />
            <Text variant="bodyStrong" tone="brand" numberOfLines={1} style={styles.actionValue}>
              {action.value}
            </Text>
          </Pressable>
        ))}
      </View>
    </View>
  );
});

const styles = StyleSheet.create({
  card: {
    gap: space.sm,
    marginTop: space.xl,
    padding: space.lg,
    borderRadius: radius.lg,
    backgroundColor: colors.bg.brandSubtle,
  },
  row: {
    // One channel per line now that each carries its value: side by side the
    // numbers truncate on any phone.
    gap: space.xs,
  },
  actionValue: {
    flex: 1,
  },
  action: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: space.sm,
    // One line of text, so the row is the touch target and nothing more.
    // Unlike the thin band at the foot of the screen, this is where someone
    // who has just paid goes to ask a question, so the 44pt stays.
    minHeight: sizing.touchTarget,
    paddingVertical: space.xs,
    paddingHorizontal: space.md,
    borderRadius: radius.md,
    backgroundColor: colors.bg.surface,
  },
  pressed: {
    opacity: 0.8,
  },
});
